import {
  Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, ViewChild
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Option } from 'src/app/settings/model/value-field';
import { PanelTriggerDirective } from '../../directive/panel-trigger.directive';

@Component({
  selector: 'app-multiselect-filter-form',
  templateUrl: './multiselect-filter-form.component.html',
  styleUrls: ['./multiselect-filter-form.component.scss']
})
export class MultiselectFilterFormComponent implements OnInit, OnDestroy {

    @Input() options: Option[];
    @Input() label: string;
    @Input() controlName: string;
    @Input() parentForm: FormGroup;

    /** Domyślnie pole ma lupę; gospodarz gasi ją, gdy szukanie nazywa już coś obok. */
    @Input() showSearchIcon = true;

    /** Prawy przycisk nad rozwiniętą listą; bez podpiętej obsługi zostaje menu przeglądarki. */
    @Output() panelContextMenu = new EventEmitter<MouseEvent>();

    @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

    searchControl: FormControl = new FormControl();
    key = '';

    private listOpen = false;

    private destroy$: Subject<void> = new Subject();

    private escapeHandler: ((event: KeyboardEvent) => void) | null = null;

    constructor(private zone: NgZone) {}

    ngOnInit(): void {
      this.control?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (this.control?.value == null) this.key = '';
          this.showSelection();
        });
      this.showSelection();
    }

    ngOnDestroy(): void {
      this.unbindEscape();
      this.destroy$.next();
    }

    onSearchChange(key: string, trigger?: PanelTriggerDirective): void {
      this.key = key;

      if (trigger && !trigger.isOpen) trigger.open();
    }

    currentValue(): string[] {
      const value = this.control?.value;
      return Array.isArray(value) ? value : [];
    }

    setValue(value: string[]): void {
      this.control.patchValue(value);
      this.control.markAsTouched();
    }

    onListOpened(trigger?: PanelTriggerDirective): void {
      this.listOpen = true;
      this.searchControl.setValue(this.key, { emitEvent: false });
      this.bindEscape(trigger);
    }

    onListClosed(): void {
      this.listOpen = false;
      this.unbindEscape();
      if (this.inputFocused()) this.searchControl.setValue(this.key, { emitEvent: false });
      else this.showSelection();
    }

    onSearchBlur(): void {
      this.showSelection();
    }

    onEscapeWhenClosed(): void {
      if (this.key) {
        this.clearSearch();
        return;
      }
      this.searchInput?.nativeElement.blur();
    }

    private bindEscape(trigger?: PanelTriggerDirective): void {
      if (!trigger) return;
      this.unbindEscape();
      this.escapeHandler = (event: KeyboardEvent) => {
        if (event.key !== 'Escape' || !trigger.isOpen) return;
        event.stopPropagation();
        this.zone.run(() => this.key ? this.clearSearch() : trigger.close());
      };
      document.addEventListener('keydown', this.escapeHandler, true);
    }

    private unbindEscape(): void {
      if (!this.escapeHandler) return;
      document.removeEventListener('keydown', this.escapeHandler, true);
      this.escapeHandler = null;
    }

    private clearSearch(): void {
      this.key = '';
      this.searchControl.setValue('', { emitEvent: false });
      this.searchInput?.nativeElement.focus();
    }

    private inputFocused(): boolean {
      return !!this.searchInput && document.activeElement === this.searchInput.nativeElement;
    }

    controlError(): string {
      const errors = this.control?.errors;
      if (errors) {
        this.searchControl.setErrors(errors);
        return Object.keys(errors)[0];
      }
      this.searchControl.setErrors(null);
      return null;
    }

    private get control(): AbstractControl {
      return this.parentForm?.get(this.controlName);
    }

    private showSelection(): void {
      if (this.listOpen) return;
      const selected = this.currentValue();
      this.searchControl.setValue(selected.length ? selected.join(', ') : '', { emitEvent: false });
    }
}
